import {memo, useMemo} from "react";
import {Badge, Box, Flex, HStack, Heading, Stack, Text} from "@chakra-ui/react";
import {
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from "../../../../../components/ui/accordion";
import {
  AUDIT_PROCEDURES,
  AUDIT_PROGRAMME_FSA_NARRATIVES,
  FINANCIAL_STATEMENT_AREAS,
  PROCESSES,
  ENTITY_LEVEL_CONTROLS,
  COTABDS,
} from "../mockData";
import type {
  AuditProcedure,
  CollaborationProps,
  EvidenceTechnique,
  FinancialStatementArea,
  TestingMethodology,
} from "../types";
import {COTABD_TYPE_COLORS} from "../types";
import {ReviewsWidget, CommentsWidget} from "./CollaborationWidget";
import {StatusButton, LockToggle, ExportButton} from "./ElcHeader";
import {EntitySummary} from "./EntitySummary";
import {FsaCotabdHeader, MockFilterBar} from "./FsaContent";
import {PROCESS_SUMMARIES} from "./ProcessSummary";
import {ELC_SUMMARIES} from "./ElcPages";
import {DataGrid} from "./DataGrid";

// --- Shared constants ---

const METHODOLOGY_COLORS: Record<TestingMethodology, string> = {
  "test-of-controls": "blue",
  "substantive-analytical": "purple",
  "substantive-detail": "green",
};

const METHODOLOGY_LABELS: Record<TestingMethodology, string> = {
  "test-of-controls": "Test of Controls",
  "substantive-analytical": "Substantive Analytical",
  "substantive-detail": "Substantive Detail",
};

const TECHNIQUE_LABELS: Record<EvidenceTechnique, string> = {
  inquiry: "Inquiry",
  observation: "Observation",
  inspection: "Inspection",
  reperformance: "Reperformance",
  confirmation: "Confirmation",
};

const TIMING_LABELS: Record<string, string> = {interim: "Interim", "year-end": "Year-End", both: "Both"};

const PROCEDURE_TABLE_PX = 4;
const PROCEDURE_GRID_COLUMNS = "60px 1fr 140px 80px 130px 130px";

// --- Shared components ---

const PageHeader = memo(({title, ...collaborationProps}: {title: string} & CollaborationProps) => (
  <Box px={6} py={4} borderBottom="1px solid {colors.gray.200}">
    <Flex align="center" gap={2}>
      <Heading size="sm" color="gray.800" flex={1}>
        {title}
      </Heading>
      <ReviewsWidget signOffs={collaborationProps.signOffs} onOpen={collaborationProps.onOpenCollaboration} />
      <CommentsWidget
        messages={collaborationProps.messages}
        unreadCount={collaborationProps.unreadCount}
        onOpen={collaborationProps.onOpenCollaboration}
      />
      <StatusButton status={collaborationProps.status} onStatusChange={collaborationProps.onStatusChange} />
      <LockToggle
        locked={collaborationProps.locked}
        lockedBy={collaborationProps.lockedBy}
        lockedAt={collaborationProps.lockedAt}
        onToggle={collaborationProps.onLockChange}
      />
      <ExportButton selection={collaborationProps.selection} onOpenReport={collaborationProps.onOpenReport} />
    </Flex>
  </Box>
));

const ProcedureTableHeader = memo(() => (
  <DataGrid.Header columns={PROCEDURE_GRID_COLUMNS} px={PROCEDURE_TABLE_PX} fontSize="xs">
    <Text>Ref</Text>
    <Text>Procedure</Text>
    <Text>Methodology</Text>
    <Text>Timing</Text>
    <Text>Extent</Text>
    <Text>Source</Text>
  </DataGrid.Header>
));

const ProcedureRow = memo(({procedure}: {procedure: AuditProcedure}) => {
  const source = useMemo(() => {
    if (procedure.elcId) {
      const elc = ENTITY_LEVEL_CONTROLS.find(e => e.id === procedure.elcId);
      return elc?.name ?? procedure.elcId;
    }
    if (procedure.processId) {
      const process = PROCESSES.find(p => p.id === procedure.processId);
      return process?.name ?? procedure.processId;
    }
    return "\u2014";
  }, [procedure.elcId, procedure.processId]);

  return (
    <DataGrid.Row columns={PROCEDURE_GRID_COLUMNS} px={PROCEDURE_TABLE_PX} alignItems="start">
      <Text fontSize="xs" fontWeight="semibold" color="blue.600" fontFamily="mono">
        {procedure.procedureNumber}
      </Text>
      <Stack gap={0.5}>
        <Text fontSize="sm" fontWeight="medium" color="gray.800">
          {procedure.title}
        </Text>
        <Text fontSize="xs" color="gray.500" lineClamp={2}>
          {procedure.description}
        </Text>
      </Stack>
      <Stack gap={0.5}>
        <Badge colorPalette={METHODOLOGY_COLORS[procedure.methodology]} size="sm" variant="subtle">
          {METHODOLOGY_LABELS[procedure.methodology]}
        </Badge>
        <Text fontSize="xs" color="gray.400">
          {procedure.techniques.map(t => TECHNIQUE_LABELS[t]).join(", ")}
        </Text>
      </Stack>
      <Text fontSize="xs" color="gray.600">
        {TIMING_LABELS[procedure.timing] ?? procedure.timing}
      </Text>
      <Text fontSize="xs" color="gray.600">
        {procedure.extent}
      </Text>
      <Text fontSize="xs" color="gray.500" lineClamp={2}>
        {source}
      </Text>
    </DataGrid.Row>
  );
});

type ProcedureGroup = {
  key: string;
  label: string;
  badge?: {label: string; colorPalette: string};
  procedures: AuditProcedure[];
};

const GroupHeaderRow = memo(({group, isFirst}: {group: ProcedureGroup; isFirst?: boolean}) => (
  <DataGrid.GroupHeader isFirst={isFirst} px={PROCEDURE_TABLE_PX}>
    <HStack gap={2}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.700">
        {group.label}
      </Text>
      {group.badge && (
        <Badge colorPalette={group.badge.colorPalette} size="sm" textTransform="capitalize">
          {group.badge.label}
        </Badge>
      )}
      <Text fontSize="xs" color="gray.500">
        {group.procedures.length} procedures
      </Text>
    </HStack>
  </DataGrid.GroupHeader>
));

export const ProcedureTable = memo(
  ({procedures, groups}: {procedures?: AuditProcedure[]; groups?: ProcedureGroup[]}) => {
    const nonEmptyGroups = groups?.filter(g => g.procedures.length > 0);
    const allProcedures = nonEmptyGroups ? nonEmptyGroups.flatMap(g => g.procedures) : (procedures ?? []);
    return (
      <Stack gap={0}>
        <MockFilterBar total={allProcedures.length} px={PROCEDURE_TABLE_PX} />
        <DataGrid.ScrollArea minW="1200px">
          <ProcedureTableHeader />
          {allProcedures.length === 0 ? (
            <DataGrid.Empty px={PROCEDURE_TABLE_PX}>No audit procedures in this section.</DataGrid.Empty>
          ) : nonEmptyGroups ? (
            nonEmptyGroups.map((group, i) => (
              <Box key={group.key}>
                <GroupHeaderRow group={group} isFirst={i === 0} />
                {group.procedures.map(p => (
                  <ProcedureRow key={p.id} procedure={p} />
                ))}
              </Box>
            ))
          ) : (
            (procedures ?? []).map(p => <ProcedureRow key={p.id} procedure={p} />)
          )}
        </DataGrid.ScrollArea>
      </Stack>
    );
  },
);

// --- By FSA ---

export const AuditProgrammeByFsa = memo((props: CollaborationProps) => {
  const proceduresByFsa = useMemo(() => {
    const map = new Map<string, AuditProcedure[]>();
    for (const fsa of FINANCIAL_STATEMENT_AREAS) {
      map.set(
        fsa.id,
        AUDIT_PROCEDURES.filter(p => p.fsaId === fsa.id),
      );
    }
    return map;
  }, []);

  const defaultOpen = useMemo(() => FINANCIAL_STATEMENT_AREAS.map(f => f.id), []);

  return (
    <Stack gap={0} minW={0}>
      <PageHeader title="Audit Programme — By Financial Statement Area" {...props} />

      <AccordionRoot multiple collapsible defaultValue={defaultOpen}>
        {FINANCIAL_STATEMENT_AREAS.map(fsa => {
          const fsaProcedures = proceduresByFsa.get(fsa.id) ?? [];
          const cotabds = COTABDS.filter(c => c.fsaId === fsa.id);
          const narrative = AUDIT_PROGRAMME_FSA_NARRATIVES[fsa.id];

          return (
            <AccordionItem key={fsa.id} value={fsa.id}>
              <AccordionItemTrigger px={6} py={3}>
                <Flex flex={1} align="center" justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" textAlign="left">
                    {fsa.name}
                  </Text>
                  <HStack gap={2}>
                    <Badge size="sm" variant="subtle" colorPalette="gray">
                      {fsaProcedures.length} procedures
                    </Badge>
                  </HStack>
                </Flex>
              </AccordionItemTrigger>
              <AccordionItemContent p={0} borderTop="1px solid {colors.gray.200}">
                <EntitySummary
                  contentKey={`audit-programme-fsa-${fsa.id}`}
                  initialContent={narrative ?? ""}
                  footer={
                    <DataGrid.Container>
                      <ProcedureTable
                        groups={cotabds.map(cotabd => ({
                          key: cotabd.id,
                          label: cotabd.name,
                          badge: {label: cotabd.type, colorPalette: COTABD_TYPE_COLORS[cotabd.type]},
                          procedures: fsaProcedures.filter(p => p.cotabdId === cotabd.id),
                        }))}
                      />
                    </DataGrid.Container>
                  }
                />
              </AccordionItemContent>
            </AccordionItem>
          );
        })}
      </AccordionRoot>
    </Stack>
  );
});

// --- By Process ---

export const AuditProgrammeByProcess = memo((props: CollaborationProps) => {
  const processesWithProcedures = useMemo(() => {
    return PROCESSES.map(process => ({
      process,
      procedures: AUDIT_PROCEDURES.filter(p => p.processId === process.id),
    })).filter(p => p.procedures.length > 0);
  }, []);

  const defaultOpen = useMemo(() => processesWithProcedures.map(p => p.process.id), [processesWithProcedures]);

  return (
    <Stack gap={0} minW={0}>
      <PageHeader title="Audit Programme — By Process" {...props} />

      <AccordionRoot multiple collapsible defaultValue={defaultOpen}>
        {processesWithProcedures.map(({process, procedures}) => {
          const narrative = PROCESS_SUMMARIES[process.id];
          const fsas = FINANCIAL_STATEMENT_AREAS.filter(f => process.fsaIds.includes(f.id));

          return (
            <AccordionItem key={process.id} value={process.id}>
              <AccordionItemTrigger px={6} py={3}>
                <Flex flex={1} align="center" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold" textAlign="left">
                    {process.name}
                  </Text>
                  <HStack gap={1} ml={2}>
                    {fsas.map(fsa => (
                      <Badge key={fsa.id} size="sm" variant="outline" colorPalette="blue">
                        {fsa.name}
                      </Badge>
                    ))}
                  </HStack>
                  <Badge size="sm" variant="subtle" colorPalette="gray" ml="auto">
                    {procedures.length} procedures
                  </Badge>
                </Flex>
              </AccordionItemTrigger>
              <AccordionItemContent p={0} borderTop="1px solid {colors.gray.200}">
                <EntitySummary
                  contentKey={`audit-programme-process-${process.id}`}
                  initialContent={narrative ?? ""}
                  footer={
                    <DataGrid.Container>
                      <ProcedureTable procedures={procedures} />
                    </DataGrid.Container>
                  }
                />
              </AccordionItemContent>
            </AccordionItem>
          );
        })}
      </AccordionRoot>
    </Stack>
  );
});

// --- By ELC ---

export const AuditProgrammeByElc = memo((props: CollaborationProps) => {
  const elcsWithProcedures = useMemo(() => {
    return ENTITY_LEVEL_CONTROLS.map(elc => ({
      elc,
      procedures: AUDIT_PROCEDURES.filter(p => p.elcId === elc.id),
    })).filter(p => p.procedures.length > 0);
  }, []);

  const defaultOpen = useMemo(() => elcsWithProcedures.map(p => p.elc.id), [elcsWithProcedures]);

  return (
    <Stack gap={0} minW={0}>
      <PageHeader title="Audit Programme — Entity-Level Controls" {...props} />

      <AccordionRoot multiple collapsible defaultValue={defaultOpen}>
        {elcsWithProcedures.map(({elc, procedures}) => {
          const narrative = ELC_SUMMARIES[elc.id];
          return (
            <AccordionItem key={elc.id} value={elc.id}>
              <AccordionItemTrigger px={6} py={3}>
                <Flex flex={1} align="center" justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" textAlign="left">
                    {elc.name}
                  </Text>
                  <Badge size="sm" variant="subtle" colorPalette="gray">
                    {procedures.length} procedures
                  </Badge>
                </Flex>
              </AccordionItemTrigger>
              <AccordionItemContent p={0} borderTop="1px solid {colors.gray.200}">
                <EntitySummary
                  contentKey={`audit-programme-elc-${elc.id}`}
                  initialContent={narrative ?? ""}
                  footer={
                    <DataGrid.Container>
                      <ProcedureTable procedures={procedures} />
                    </DataGrid.Container>
                  }
                />
              </AccordionItemContent>
            </AccordionItem>
          );
        })}
      </AccordionRoot>
    </Stack>
  );
});

// --- FSA Audit Strategy (per-FSA sub-page) ---

export const FsaAuditStrategy = memo(
  ({
    fsa,
    allocatedMateriality,
    trivialThreshold,
    ...collaborationProps
  }: {fsa: FinancialStatementArea; allocatedMateriality?: number; trivialThreshold?: number} & CollaborationProps) => {
    const narrative = AUDIT_PROGRAMME_FSA_NARRATIVES[fsa.id] ?? "";
    const fsaProcedures = useMemo(() => AUDIT_PROCEDURES.filter(p => p.fsaId === fsa.id), [fsa.id]);
    const cotabds = useMemo(() => COTABDS.filter(c => c.fsaId === fsa.id), [fsa.id]);
    const groups = useMemo<ProcedureGroup[]>(
      () =>
        cotabds.map(cotabd => ({
          key: cotabd.id,
          label: cotabd.name,
          badge: {label: cotabd.type, colorPalette: COTABD_TYPE_COLORS[cotabd.type]},
          procedures: fsaProcedures.filter(p => p.cotabdId === cotabd.id),
        })),
      [cotabds, fsaProcedures],
    );

    return (
      <EntitySummary
        contentKey={`fsa-${fsa.id}-strategy`}
        header={
          <FsaCotabdHeader
            fsaName={fsa.name}
            {...collaborationProps}
            allocatedMateriality={allocatedMateriality}
            trivialThreshold={trivialThreshold}
          />
        }
        initialContent={narrative}
        footer={
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={3}>
              Planned Audit Procedures
            </Text>
            <DataGrid.Container>
              <ProcedureTable groups={groups} />
            </DataGrid.Container>
          </Box>
        }
      />
    );
  },
);
